import type { ParticipantSelf, PollResults, SessionState, VoteAck, VoteChoice, Word } from "@hali-shares/deck-schema";
import { useCallback, useEffect, useRef, useState } from "react";
import { type LiveSocket, connectLive } from "./socket";

export type PhoneSession = {
  connected: boolean;
  state: SessionState | null;
  /** Latest results per block, only ever sent while that block is revealed. */
  results: Record<string, PollResults>;
  /** What the server recorded as this phone's vote, per block. */
  acks: Record<string, VoteChoice>;
  /** Last refusal for a vote ("Voting is locked"), cleared on the next ack. */
  voteError: string | null;
  /** The server doesn't know this phone (cookie gone): show the join screen again. */
  needsJoin: boolean;
  vote: (blockId: string, choice: VoteChoice) => void;
  /** Score, place and nickname (quiz leaderboard). */
  self: ParticipantSelf | null;
  setNickname: (nickname: string) => void;
  nicknameError: string | null;
  /** Open polls: everyone's answers while voting, and the keys this phone upvoted. */
  answers: Record<string, Word[]>;
  upvoted: Record<string, string[]>;
  upvote: (blockId: string, key: string, on: boolean) => void;
};

/** The phone renders entirely from session:state, poll:results and its own vote:ack. */
/** `attempt` bumps after a (re)join so the hook reconnects with the new cookie. */
export function usePhoneSession(code: string | null, attempt = 0): PhoneSession {
  const socket = useRef<LiveSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [state, setState] = useState<SessionState | null>(null);
  const [results, setResults] = useState<Record<string, PollResults>>({});
  const [acks, setAcks] = useState<Record<string, VoteChoice>>({});
  const [voteError, setVoteError] = useState<string | null>(null);
  const [self, setSelf] = useState<ParticipantSelf | null>(null);
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, Word[]>>({});
  const [upvoted, setUpvoted] = useState<Record<string, string[]>>({});
  const [refusedAttempt, setRefusedAttempt] = useState(-1);

  useEffect(() => {
    if (!code) return;
    const s = connectLive({ role: "audience", code });
    socket.current = s;
    s.on("connect", () => setConnected(true));
    s.on("disconnect", () => setConnected(false));
    s.on("session:state", setState);
    s.on("poll:results", (r) => setResults((all) => ({ ...all, [r.blockId]: r })));
    s.on("vote:ack", (a: VoteAck) => {
      setAcks((all) => ({ ...all, [a.blockId]: a.choice }));
      setVoteError(null);
    });
    s.on("participant:self", (p) => {
      setSelf(p);
      setNicknameError(null);
    });
    s.on("answers:list", (a) => setAnswers((all) => ({ ...all, [a.blockId]: a.answers })));
    s.on("upvote:ack", (a) => setUpvoted((all) => ({ ...all, [a.blockId]: a.keys })));
    s.on("live:error", (e) => {
      if (e.event === "connect") {
        if (e.message === "Join the session first") setRefusedAttempt(attempt);
        s.disconnect();
      } else if (e.event === "vote:cast" || e.event === "answer:upvote") setVoteError(e.message);
      else if (e.event === "participant:nickname") setNicknameError(e.message);
    });
    return () => {
      s.disconnect();
      socket.current = null;
    };
  }, [code, attempt]);

  const vote = useCallback((blockId: string, choice: VoteChoice) => {
    socket.current?.emit("vote:cast", { blockId, choice });
  }, []);

  const setNickname = useCallback((nickname: string) => {
    socket.current?.emit("participant:nickname", { nickname });
  }, []);
  const upvote = useCallback((blockId: string, key: string, on: boolean) => {
    socket.current?.emit("answer:upvote", { blockId, key, on });
  }, []);

  return {
    connected,
    state,
    results,
    acks,
    voteError,
    needsJoin: refusedAttempt === attempt,
    vote,
    self,
    setNickname,
    nicknameError,
    answers,
    upvoted,
    upvote,
  };
}
